
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import { useAuthSession } from '@/hooks/useAuthSession';
import { SecurityAlertModal } from '@/components/security/SecurityAlertModal';
import { logSecurityEvent } from '@/utils/securityUtils';

interface SecurityMonitoringContextType {
  isSecurityEnabled: boolean;
  setSecurityEnabled: (enabled: boolean) => void;
  forceSecurityCheck: () => Promise<void>;
  lockAccount: () => Promise<void>;
  detectedSuspiciousActivity: boolean;
}

const SecurityMonitoringContext = createContext<SecurityMonitoringContextType>({
  isSecurityEnabled: true,
  setSecurityEnabled: () => {},
  forceSecurityCheck: async () => {},
  lockAccount: async () => {},
  detectedSuspiciousActivity: false
});

export const useSecurityMonitoring2 = () => useContext(SecurityMonitoringContext);

interface SecurityMonitoringProviderProps {
  children: React.ReactNode;
}

export const SecurityMonitoringProvider: React.FC<SecurityMonitoringProviderProps> = ({ children }) => {
  const [isSecurityEnabled, setSecurityEnabled] = useState(true);
  const [showSecurityAlert, setShowSecurityAlert] = useState(false);
  const { userId } = useAuthSession();
  
  // Use the security monitoring hook
  const {
    detectedSuspiciousActivity,
    forceSecurityCheck,
    lockAccount
  } = useSecurityMonitoring({
    enabled: isSecurityEnabled,
    onSuspiciousActivity: () => {
      setShowSecurityAlert(true);
    }
  });
  
  // Show the security alert when suspicious activity is detected
  useEffect(() => {
    if (detectedSuspiciousActivity) {
      setShowSecurityAlert(true);
    }
  }, [detectedSuspiciousActivity]);
  
  // Handle the secure account action
  const handleSecureAccount = async () => {
    if (userId) {
      await logSecurityEvent(
        userId,
        'security_action_taken',
        'User took action to secure their account'
      );
    }
    
    // Redirect to settings page where user can change password, enable 2FA, etc.
    window.location.href = '/settings?security=true';
  };
  
  // Handle the ignore action
  const handleIgnore = async () => {
    if (userId) {
      await logSecurityEvent(
        userId,
        'security_alert_ignored',
        'User ignored security alert'
      );
    }
  };
  
  return (
    <SecurityMonitoringContext.Provider
      value={{
        isSecurityEnabled,
        setSecurityEnabled,
        forceSecurityCheck,
        lockAccount,
        detectedSuspiciousActivity
      }}
    >
      {children}
      
      <SecurityAlertModal
        open={showSecurityAlert}
        onClose={() => setShowSecurityAlert(false)}
        title="Unusual Account Activity Detected"
        description="We've noticed some unusual activity on your account. We recommend taking steps to secure your account."
        severity="medium"
        onSecureAccount={handleSecureAccount}
        onIgnore={handleIgnore}
      />
    </SecurityMonitoringContext.Provider>
  );
};
